import React, { useEffect, useId, useRef } from 'react';
import {
  ButtonIcon,
  Icon,
  MobileDrawer,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { useInvertedLocalize } from '../../../utils/localization';

import invertedMessages from './messages';
import PopoverContent from './PopoverContent';

import './PopoverProgressBreakdown.css';

function PopoverProgressBreakdown({
  closePopover,
  isProgressBarOpen,
  setIsProgressBarOpen,
  isMobileWidth,
  label,
}) {
  const dialogRef = useRef(null);
  const headingId = useId();
  const l = useInvertedLocalize(invertedMessages);

  useEffect(() => {
    if (isProgressBarOpen && !isMobileWidth && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isProgressBarOpen, isMobileWidth]);

  useEffect(() => {
    if (!isProgressBarOpen || isMobileWidth) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closePopover();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isProgressBarOpen, isMobileWidth, closePopover]);

  if (isMobileWidth) {
    return (
      <MobileDrawer
        isSwipeEnabled
        isOpen={isProgressBarOpen}
        setIsOpen={setIsProgressBarOpen}
        className='PopoverProgressBreakdown-mobileDrawer'
        content={
          <>
            <Typography
              kind='h2'
              mobile='h3-d'
              className='PopoverProgressBreakdown-title'>
              {label}
            </Typography>
            <PopoverContent />
          </>
        }
      />
    );
  }

  return (
    <div
      className='PopoverProgressBreakdown'
      role='dialog'
      aria-modal='true'
      aria-labelledby={headingId}
      ref={dialogRef}
      tabIndex='-1'>
      <div className='PopoverProgressBreakdown-header'>
        <Typography
          id={headingId}
          kind='h2'
          mobile='h3-d'
          className='PopoverProgressBreakdown-title'>
          {label}
        </Typography>
        <ButtonIcon
          size='small'
          kind='ghost'
          onClick={closePopover}
          icon={<Icon kind='Close' color='neutral-1000' size='small' />}
          ariaLabel={l('Close')}
        />
      </div>
      <PopoverContent />
    </div>
  );
}

PopoverProgressBreakdown.propTypes = {
  closePopover: PropTypes.func.isRequired,
  isProgressBarOpen: PropTypes.bool.isRequired,
  setIsProgressBarOpen: PropTypes.func.isRequired,
  isMobileWidth: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
};

PopoverProgressBreakdown.defaultProps = {};

export default PopoverProgressBreakdown;
